import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Button,
  Collapse,
  Divider,
  Alert,
  Tooltip,
  Grid,
  FormControlLabel,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  Functions as FunctionsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon,
  AccessTime as TimestampIcon,
  TextFields as RegexIcon,
  Calculate as MathIcon,
  MergeType as ConcatIcon,
  Rule as ConditionalIcon,
} from '@mui/icons-material';
import {
  ColumnFormula,
  FormulaType,
  FormulaTrigger,
  ConditionalRule,
  TableColumn,
} from '../types/table.types';

interface FormulaEditorProps {
  /** Текущая формула (null = нет формулы) */
  value: ColumnFormula | null | undefined;
  /** Callback при изменении */
  onChange: (formula: ColumnFormula | null) => void;
  /** Все колонки таблицы (для выбора source) */
  availableColumns: { name: string; type: string }[];
  /** Имя текущей колонки (исключаем из source) */
  currentColumnName?: string;
  /** Заблокировать редактирование */
  disabled?: boolean;
}

const FORMULA_TYPE_CONFIG: Record<FormulaType, {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}> = {
  [FormulaType.SET_TIMESTAMP]: {
    label: 'Timestamp',
    description: 'Установить текущую дату/время при изменении источника',
    icon: <TimestampIcon fontSize="small" />,
    color: '#ff9800',
  },
  [FormulaType.REGEX_EXTRACT]: {
    label: 'Regex',
    description: 'Извлечь значение из другой колонки по регулярному выражению',
    icon: <RegexIcon fontSize="small" />,
    color: '#e91e63',
  },
  [FormulaType.COPY_VALUE]: {
    label: 'Копировать',
    description: 'Скопировать значение из другой колонки',
    icon: <CopyIcon fontSize="small" />,
    color: '#2196f3',
  },
  [FormulaType.CONCAT]: {
    label: 'Объединить',
    description: 'Склеить значения нескольких колонок по шаблону',
    icon: <ConcatIcon fontSize="small" />,
    color: '#4caf50',
  },
  [FormulaType.MATH]: {
    label: 'Математика',
    description: 'Вычислить значение по формуле (+ - * /)',
    icon: <MathIcon fontSize="small" />,
    color: '#9c27b0',
  },
  [FormulaType.CONDITIONAL]: {
    label: 'Условие',
    description: 'Подставить значение по условию (если ... то ...)',
    icon: <ConditionalIcon fontSize="small" />,
    color: '#00bcd4',
  },
};

const TRIGGER_OPTIONS: { value: FormulaTrigger; label: string }[] = [
  { value: FormulaTrigger.ALWAYS, label: 'Всегда (создание + обновление)' },
  { value: FormulaTrigger.ON_CREATE, label: 'Только при создании' },
  { value: FormulaTrigger.ON_UPDATE, label: 'Только при обновлении' },
];

const CONDITION_OPERATORS: { value: string; label: string }[] = [
  { value: 'eq', label: '= равно' },
  { value: 'ne', label: '≠ не равно' },
  { value: 'gt', label: '> больше' },
  { value: 'gte', label: '≥ больше или равно' },
  { value: 'lt', label: '< меньше' },
  { value: 'lte', label: '≤ меньше или равно' },
  { value: 'contains', label: 'Содержит' },
  { value: 'regex', label: 'Regex' },
  { value: 'empty', label: 'Пусто' },
  { value: 'not_empty', label: 'Не пусто' },
];

export default function FormulaEditor({
  value,
  onChange,
  availableColumns,
  currentColumnName,
  disabled = false,
}: FormulaEditorProps) {
  const [expanded, setExpanded] = useState(!!value);
  const [formula, setFormula] = useState<ColumnFormula | null>(value || null);

  // Колонки без текущей
  const sourceColumns = availableColumns.filter(c => c.name !== currentColumnName);

  useEffect(() => {
    setFormula(value || null);
    setExpanded(!!value);
  }, [value]);

  const handleEnable = () => {
    const newFormula: ColumnFormula = {
      type: FormulaType.COPY_VALUE,
      sourceColumns: [],
      trigger: FormulaTrigger.ALWAYS,
      overwriteManual: false,
    };
    setFormula(newFormula);
    setExpanded(true);
    onChange(newFormula);
  };

  const handleDisable = () => {
    setFormula(null);
    setExpanded(false);
    onChange(null);
  };

  const update = (patch: Partial<ColumnFormula>) => {
    const updated = { ...formula!, ...patch };
    setFormula(updated);
    onChange(updated);
  };

  const handleTypeChange = (type: FormulaType) => {
    const base: ColumnFormula = {
      type,
      sourceColumns: formula?.sourceColumns || [],
      trigger: formula?.trigger || FormulaTrigger.ALWAYS,
      overwriteManual: formula?.overwriteManual || false,
    };

    // Сброс type-specific полей
    if (type === FormulaType.REGEX_EXTRACT) {
      base.pattern = formula?.pattern || '';
      base.groupIndex = 1;
    }
    if (type === FormulaType.CONCAT) {
      base.template = formula?.template || '';
    }
    if (type === FormulaType.MATH) {
      base.expression = formula?.expression || '';
    }
    if (type === FormulaType.CONDITIONAL) {
      base.conditions = formula?.conditions || [];
    }

    setFormula(base);
    onChange(base);
  };

  const handleToggleSource = (colName: string) => {
    if (!formula) return;
    const current = formula.sourceColumns || [];
    const updated = current.includes(colName)
      ? current.filter(c => c !== colName)
      : [...current, colName];
    update({ sourceColumns: updated });
  };

  // ===== Conditional rules =====
  const addCondition = () => {
    const conditions = [...(formula?.conditions || [])];
    conditions.push({
      column: sourceColumns[0]?.name || '',
      operator: 'eq',
      compareValue: '',
      resultValue: '',
    });
    update({ conditions });
  };

  const updateCondition = (index: number, patch: Partial<ConditionalRule>) => {
    const conditions = [...(formula?.conditions || [])];
    conditions[index] = { ...conditions[index], ...patch };
    update({ conditions });
  };

  const removeCondition = (index: number) => {
    const conditions = (formula?.conditions || []).filter((_, i) => i !== index);
    update({ conditions });
  };

  if (!formula) {
    return (
      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.2s',
          '&:hover': disabled ? {} : {
            borderColor: '#667eea',
            bgcolor: 'rgba(102, 126, 234, 0.04)',
          },
        }}
        onClick={disabled ? undefined : handleEnable}
      >
        <FunctionsIcon sx={{ color: 'text.secondary', mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          Добавить формулу (автозаполнение)
        </Typography>
      </Box>
    );
  }

  const typeConfig = FORMULA_TYPE_CONFIG[formula.type];

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: `${typeConfig.color}40`,
        borderRadius: 2,
        overflow: 'visible',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          px: 2,
          bgcolor: `${typeConfig.color}08`,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FunctionsIcon sx={{ color: typeConfig.color, fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Формула
          </Typography>
          <Chip
            icon={typeConfig.icon as React.ReactElement}
            label={typeConfig.label}
            size="small"
            sx={{
              bgcolor: `${typeConfig.color}15`,
              color: typeConfig.color,
              fontWeight: 600,
              '& .MuiChip-icon': { color: typeConfig.color },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleDisable();
            }}
            disabled={disabled}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </Box>
      </Box>

      <Collapse in={expanded}>
        <CardContent sx={{ pt: 2 }}>
          {/* Тип формулы */}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
            Тип формулы
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
            {Object.entries(FORMULA_TYPE_CONFIG).map(([type, config]) => (
              <Chip
                key={type}
                icon={config.icon as React.ReactElement}
                label={config.label}
                size="small"
                variant={formula.type === type ? 'filled' : 'outlined'}
                onClick={() => !disabled && handleTypeChange(type as FormulaType)}
                disabled={disabled}
                sx={{
                  cursor: disabled ? 'default' : 'pointer',
                  ...(formula.type === type
                    ? {
                        bgcolor: `${config.color}20`,
                        color: config.color,
                        borderColor: config.color,
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: config.color },
                      }
                    : {
                        '&:hover': {
                          bgcolor: `${config.color}10`,
                          borderColor: config.color,
                        },
                      }),
                }}
              />
            ))}
          </Box>

          <Alert severity="info" sx={{ mb: 2, py: 0.5, borderRadius: 1.5 }} icon={<InfoIcon fontSize="small" />}>
            <Typography variant="caption">{typeConfig.description}</Typography>
          </Alert>

          {/* Колонки-источники */}
          {formula.type !== FormulaType.CONDITIONAL && (
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                Колонки-источники
              </Typography>
              {sourceColumns.length === 0 ? (
                <Typography variant="caption" color="text.disabled">
                  Нет доступных колонок. Сначала добавьте другие колонки.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {sourceColumns.map(col => {
                    const selected = formula.sourceColumns.includes(col.name);
                    return (
                      <Chip
                        key={col.name}
                        label={`${col.name} (${col.type})`}
                        size="small"
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => !disabled && handleToggleSource(col.name)}
                        disabled={disabled}
                        color={selected ? 'primary' : 'default'}
                        sx={{ cursor: disabled ? 'default' : 'pointer' }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Type-specific settings */}
          {formula.type === FormulaType.REGEX_EXTRACT && (
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Регулярное выражение"
                fullWidth
                size="small"
                value={formula.pattern || ''}
                onChange={e => update({ pattern: e.target.value })}
                disabled={disabled}
                placeholder="Пример: ID-(\d+)"
                helperText="Первая capture group () будет результатом"
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace' } }}
              />
              <TextField
                label="Номер группы"
                type="number"
                size="small"
                value={formula.groupIndex ?? 1}
                onChange={e => update({ groupIndex: Number(e.target.value) })}
                disabled={disabled}
                inputProps={{ min: 0 }}
                sx={{ width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                helperText="0 = весь match"
              />
            </Box>
          )}

          {formula.type === FormulaType.CONCAT && (
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Шаблон"
                fullWidth
                size="small"
                value={formula.template || ''}
                onChange={e => update({ template: e.target.value })}
                disabled={disabled}
                placeholder="{first_name} {last_name}"
                helperText="Используйте {имя_колонки} для подстановки значений"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace' } }}
              />
              {formula.sourceColumns.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    Вставить:
                  </Typography>
                  {formula.sourceColumns.map(col => (
                    <Chip
                      key={col}
                      label={`{${col}}`}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        if (disabled) return;
                        update({ template: (formula.template || '') + `{${col}}` });
                      }}
                      sx={{ cursor: 'pointer', fontFamily: 'monospace', height: 22, fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {formula.type === FormulaType.MATH && (
            <Box sx={{ mb: 2 }}>
              <TextField
                label="Выражение"
                fullWidth
                size="small"
                value={formula.expression || ''}
                onChange={e => update({ expression: e.target.value })}
                disabled={disabled}
                placeholder="price * quantity"
                helperText="Используйте имена колонок и операции: + - * / ( ) %"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace' } }}
              />
              {formula.sourceColumns.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    Вставить:
                  </Typography>
                  {formula.sourceColumns.map(col => (
                    <Chip
                      key={col}
                      label={col}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        if (disabled) return;
                        update({ expression: (formula.expression || '') + col });
                      }}
                      sx={{ cursor: 'pointer', fontFamily: 'monospace', height: 22, fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {formula.type === FormulaType.CONDITIONAL && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Условия (проверяются по порядку)
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addCondition}
                  disabled={disabled}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Добавить условие
                </Button>
              </Box>

              {(formula.conditions || []).map((rule, idx) => (
                <Card
                  key={idx}
                  variant="outlined"
                  sx={{ mb: 1, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(0,0,0,0.02)' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Если
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeCondition(idx)}
                      disabled={disabled}
                      sx={{ ml: 'auto' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Grid container spacing={1}>
                    <Grid size={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Колонка</InputLabel>
                        <Select
                          value={rule.column}
                          label="Колонка"
                          onChange={e => updateCondition(idx, { column: e.target.value })}
                          disabled={disabled}
                        >
                          {sourceColumns.map(c => (
                            <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Оператор</InputLabel>
                        <Select
                          value={rule.operator}
                          label="Оператор"
                          onChange={e => updateCondition(idx, { operator: e.target.value as any })}
                          disabled={disabled}
                        >
                          {CONDITION_OPERATORS.map(op => (
                            <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {!['empty', 'not_empty'].includes(rule.operator) && (
                      <Grid size={2.5}>
                        <TextField
                          label="Значение"
                          size="small"
                          fullWidth
                          value={rule.compareValue ?? ''}
                          onChange={e => updateCondition(idx, { compareValue: e.target.value })}
                          disabled={disabled}
                        />
                      </Grid>
                    )}

                    <Grid size={['empty', 'not_empty'].includes(rule.operator) ? 5 : 2.5}>
                      <TextField
                        label="Результат"
                        size="small"
                        fullWidth
                        value={rule.resultValue ?? ''}
                        onChange={e => updateCondition(idx, { resultValue: e.target.value })}
                        disabled={disabled}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(76, 175, 80, 0.05)',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}

              {(formula.conditions || []).length === 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                  Нет условий. Нажмите «Добавить условие».
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Триггер + опции */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6}}>
              <TextField
                select
                label="Когда срабатывает"
                size="small"
                fullWidth
                value={formula.trigger}
                onChange={e => update({ trigger: e.target.value as FormulaTrigger })}
                disabled={disabled}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                {TRIGGER_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6}}>
              <TextField
                label="Значение по умолчанию"
                size="small"
                fullWidth
                value={formula.fallbackValue ?? ''}
                onChange={e => update({ fallbackValue: e.target.value || undefined })}
                disabled={disabled}
                placeholder="Если формула не смогла вычислить"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formula.overwriteManual || false}
                    onChange={e => update({ overwriteManual: e.target.checked })}
                    disabled={disabled}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    Перезаписывать ручное значение
                    <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                      (если выключено — ручной ввод имеет приоритет)
                    </Typography>
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );
}